import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit3, FileText, Info } from "lucide-react";
import { listSagaDefinitions } from "../services/saga-definition";
import type { SagaDefinitionView } from "../types/saga-definition";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { RequireRole } from "../components/RequireRole";

interface NameGroup {
  name: string;
  active: SagaDefinitionView | null;
  totalVersions: number;
  latestCreatedAt: string;
}

export default function SagaDefinitionList() {
  const [all, setAll] = useState<SagaDefinitionView[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await listSagaDefinitions();
        if (alive) setAll(rows);
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : "Falha ao listar");
      }
    })();
    return () => { alive = false; };
  }, []);

  const grouped: NameGroup[] = all
    ? Object.values(
        all.reduce((acc, row) => {
          const g = acc[row.name] ?? {
            name: row.name,
            active: null,
            totalVersions: 0,
            latestCreatedAt: row.createdAt,
          };
          g.totalVersions++;
          if (row.active) g.active = row;
          if (row.createdAt > g.latestCreatedAt) g.latestCreatedAt = row.createdAt;
          acc[row.name] = g;
          return acc;
        }, {} as Record<string, NameGroup>),
      ).sort((a, b) => a.name.localeCompare(b.name))
    : [];

  return (
    <>
      <PageHeader
        title="Saga definitions"
        subtitle="Composições de saga gerenciadas via DB (Fase 2 — SPEC-20260507-0135)"
        actions={
          <RequireRole anyOf={["platform-master", "platform-admin"]}>
            <Link to="/sagas/definitions/new">
              <Button size="sm" variant="primary"><Plus size={14} /> Nova definition</Button>
            </Link>
          </RequireRole>
        }
      />

      <div className="p-6 space-y-3">
        {/* SPEC-20260507-0136 (Fase 3) UX: dica explicando o modelo conceitual. */}
        <div className="rounded-md border border-border-subtle bg-surface-subtle/40 p-3 flex items-start gap-2 text-xs leading-relaxed">
          <Info size={14} className="text-muted-foreground shrink-0 mt-0.5" />
          <div className="flex-1 text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Modelo conceitual</p>
            <p>
              Cada saga definition é uma <strong>composição versionada</strong> de steps pré-construídos.{" "}
              Você pode reordenar/adicionar/remover steps via UI (clica em uma saga + "Editar (nova versão)") —{" "}
              isso cria nova versão sem afetar sagas em flight.
            </p>
            <p className="mt-1">
              <strong>Steps são código TS no backend</strong> (catálogo via{" "}
              <code className="font-mono-tight bg-surface px-1 py-0.5 rounded">/api/step-catalog</code>).{" "}
              Pra adicionar step novo (lógica nova) é PR no orchestrator. Pra integrar sistema externo via HTTP,{" "}
              use <code className="font-mono-tight bg-surface px-1 py-0.5 rounded">http.invoke</code> (já no catálogo).
            </p>
          </div>
        </div>

        {!all && !err && <p className="text-sm text-muted-foreground">Carregando…</p>}
        {err && <div className="p-3 rounded-md bg-danger-soft text-danger-soft-fg text-sm">{err}</div>}

        {all && grouped.length === 0 && (
          <div className="text-sm text-muted-foreground italic py-6 text-center border border-dashed border-border rounded-md">
            Nenhuma definition cadastrada. Rode <code className="font-mono-tight">bun run seed:saga-definitions</code> no backend para popular.
          </div>
        )}

        {grouped.map((g) => (
          <Link
            key={g.name}
            to={`/sagas/definitions/${encodeURIComponent(g.name)}`}
            className="block rounded-md border border-border bg-surface p-4 hover:border-primary/40 transition-colors"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-muted-foreground shrink-0" />
                  <span className="font-medium font-mono-tight">{g.name}</span>
                  {g.active ? (
                    <span className="inline-flex items-center gap-1.5 rounded border border-success/20 bg-success-soft text-success-soft-fg px-2 py-0.5 text-xs font-medium font-mono-tight">
                      <span className="w-1.5 h-1.5 rounded-full bg-success" aria-hidden="true" />
                      active v{g.active.version}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">sem versão active</span>
                  )}
                </div>
                {g.active?.description && (
                  <p className="text-xs text-muted-foreground mt-1 truncate">{g.active.description}</p>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground whitespace-nowrap">
                <span>{g.totalVersions} versão{g.totalVersions !== 1 ? "ões" : ""}</span>
                <Edit3 size={13} />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}
